/**
 * 🔍 GlobalSearch — quick-jump finder for every area of the game.
 *
 * Renders a 🔍 trigger button (intended for the Header) that opens a
 * fullscreen modal with a search input + filtered list of every routable
 * area in the app. Fuzzy-ish match across label + keywords + description.
 *
 * Keyboard:
 *   • ⌘K / Ctrl+K toggles open
 *   • Esc closes
 *   • ↑/↓ navigate, Enter selects
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";

// 🗺️ Authoritative index of every routable area in OMNIMUSCLE.
// Keywords power the fuzzy match — add aliases here as the world grows.
const AREAS = [
  // Home & profile
  { id: "home",       label: "Home",            icon: "🏠", path: "/",                  desc: "Landing page · manifesto · plans · tools",
    keywords: ["home", "landing", "main", "start", "manifesto"] },
  { id: "profile",    label: "My Profile",      icon: "🧑", path: "/profile",           desc: "Account · streaks · badges · settings",
    keywords: ["profile", "account", "me", "settings", "badges", "streaks"] },
  { id: "corner",     label: "My Corner",       icon: "🪑", path: "/corner",            desc: "Personal hub · friends · DMs",
    keywords: ["corner", "hub", "friends", "personal"] },
  { id: "messages",   label: "Messages",        icon: "✉️", path: "/messages",          desc: "Direct messages with friends",
    keywords: ["messages", "dm", "chat", "inbox", "friends"] },
  // Game zones
  { id: "nest",       label: "Phoenix Nest",    icon: "🪺", path: "/nest",              desc: "Your home base — hatch eggs · pet management",
    keywords: ["nest", "phoenix", "home base", "eggs", "hatch"] },
  { id: "farm",       label: "Farm",            icon: "🌱", path: "/farm",              desc: "Crops · GrainHouse · chores · Embersweet",
    keywords: ["farm", "crops", "grainhouse", "embersweet", "chores"] },
  { id: "wilds",      label: "The Wilds",       icon: "🗺️", path: "/wilds",             desc: "Mines · mob hunting · veins · South Quadrant",
    keywords: ["wilds", "mines", "mobs", "hunt", "veins", "south", "quadrant"] },
  { id: "kitchen",    label: "Dave's Kitchen",  icon: "👨‍🍳", path: "/kitchen",         desc: "Cook the recipes you've learned",
    keywords: ["kitchen", "cook", "dave", "david", "recipes"] },
  { id: "blue_mage",  label: "Blue Mage Menu",  icon: "💙", path: "/blue-mage-menu",    desc: "Learn-by-eating skill menu — Vesper's path",
    keywords: ["blue mage", "vesper", "menu", "skills", "learn"] },
  { id: "codex",      label: "Codex",           icon: "📜", path: "/codex",             desc: "NPC journals — every voice that's written about you",
    keywords: ["codex", "journal", "npc", "lore", "story"] },
  { id: "misty_map",  label: "Misty Map",       icon: "🌫️", path: "/misty-map",         desc: "Discovery board — every region of Dragonious, fog-of-war style",
    keywords: ["map", "misty", "fog", "discovery", "world", "region", "explore"] },
  { id: "town_hall",  label: "Town Hall",       icon: "🏛️", path: "/town-hall",         desc: "Quests · licenses · Chief Wesley · Miss Kitty",
    keywords: ["town hall", "quests", "license", "wesley", "kitty"] },
  // 📍 Feb 2026 owner rule: Library, Gym, and Diner are ONLY reachable
  // through the Castle Courtyard hub — no direct search hits here.
  { id: "blacksmith", label: "Blacksmith",      icon: "⚒️", path: "/blacksmith",        desc: "Forge weapons + armor · luck stones",
    keywords: ["blacksmith", "smith", "forge", "weapons", "armor", "craft"] },
  { id: "pet",        label: "Phoenix Pet",     icon: "🦅", path: "/pet",               desc: "Your hatched phoenix — evolution · gear",
    keywords: ["pet", "phoenix", "evolution", "companion"] },
  { id: "constellation", label: "Constellation Shop", icon: "✨", path: "/constellation", desc: "Spend hero coins · Embersweet 2.0",
    keywords: ["constellation", "shop", "coins", "merchant", "stars"] },
  // Combat & competition
  { id: "coliseum",   label: "Coliseum",        icon: "⚔️", path: "/coliseum",          desc: "3v3 Arena · Multi-Player Dungeon",
    keywords: ["coliseum", "arena", "3v3", "mpd", "dungeon", "pvp", "combat"] },
  { id: "arena",      label: "Pet Arena",       icon: "🥊", path: "/arena",             desc: "Phoenix pet PvP",
    keywords: ["arena", "pet arena", "pvp", "pet pvp"] },
  { id: "arcade",     label: "Arcade",          icon: "🕹️", path: "/arcade",            desc: "Mini-games · beat-em-up modes",
    keywords: ["arcade", "minigame", "mini game", "beat em up", "retro"] },
  { id: "hero_class", label: "Hero Class",      icon: "🦸", path: "/hero-class",        desc: "Choose your path — Warrior · Mage · Archer · Ash Rogue",
    keywords: ["hero", "class", "warrior", "mage", "archer", "ash rogue", "blue mage"] },
  // Guild & community
  { id: "guild",      label: "Guild Hall",      icon: "🐦", path: "/guild",             desc: "Birds of a Feather · Wealth · Lever",
    keywords: ["guild", "guild hall", "wealth", "lever", "phoenix"] },
  { id: "apex",       label: "Ashen Apex",      icon: "🗼", path: "/guild/ashen-apex",  desc: "Guild Skill Tower",
    keywords: ["apex", "ashen apex", "tower", "guild skills"] },
  { id: "glimmers",   label: "Glimmer Sanctum", icon: "💎", path: "/glimmers",          desc: "Feed Glimmer Pieces · level up Glimmers",
    keywords: ["glimmer", "sanctum", "glimmers", "pieces"] },
  { id: "champions",  label: "Hall of Champions", icon: "🏆", path: "/hall-of-champions", desc: "Top community moments · 50+ reactions",
    keywords: ["champions", "hall of champions", "community", "leaderboard"] },
  { id: "phoenix",    label: "Hall of Phoenix", icon: "🏛️", path: "/hall-of-phoenix",   desc: "Founders · supporters · believers",
    keywords: ["hall of phoenix", "founders", "supporters", "believers"] },
  { id: "forums",     label: "Forums",          icon: "💬", path: "/forums",            desc: "Community threads · posts · discussions",
    keywords: ["forums", "discuss", "community", "threads", "posts"] },
  // Economy
  { id: "marketplace", label: "Marketplace",    icon: "🛒", path: "/marketplace",       desc: "Pet weapons + armor · player-to-player",
    keywords: ["marketplace", "market", "shop pet", "pet gear", "trade"] },
  { id: "auction",    label: "Auction House",   icon: "🏪", path: "/auction-house",     desc: "Bid · sell · snipe · cross-class gear",
    keywords: ["auction", "auction house", "bid", "sell", "snipe"] },
  { id: "shop",       label: "Shop",            icon: "💸", path: "/shop",              desc: "Premium tier · OmniDollars · token packs",
    keywords: ["shop", "store", "tokens", "omnidollars", "premium"] },
  { id: "plans",      label: "Plans",           icon: "📋", path: "/plans",             desc: "Subscription tiers · Premium · Founder",
    keywords: ["plans", "subscription", "pricing", "tier", "premium"] },
  // Wellness / training
  { id: "cardio",     label: "Cardio",          icon: "❤️", path: "/cardio",            desc: "Cardio tracker · sessions · streaks",
    keywords: ["cardio", "run", "running", "heart", "endurance"] },
  { id: "tracker_calendar", label: "Tracker Calendar", icon: "📅", path: "/tracker-calendar", desc: "Full month view — log missed days for food · strength · cardio",
    keywords: ["calendar", "tracker", "log", "backfill", "missed", "past", "month", "day"] },
  // Lore
  { id: "intro",      label: "Opening Cinematic", icon: "🎬", path: "/intro",           desc: "Tomb of Awakening · Phoenix Transform · Castle",
    keywords: ["intro", "cinematic", "opening", "tomb", "phoenix transform"] },
  // Support
  { id: "faq",        label: "FAQ",             icon: "❓", path: "/faq",               desc: "Common questions · troubleshooting",
    keywords: ["faq", "help", "support", "questions"] },
  { id: "supporters", label: "Supporters",      icon: "🤝", path: "/supporters",        desc: "Founding supporters · partners",
    keywords: ["supporters", "founding", "partners"] },
];

function matches(area, q) {
  if (!q) return true;
  const t = q.toLowerCase().trim();
  if (!t) return true;
  const haystack = [
    area.label,
    area.desc,
    ...(area.keywords || []),
  ].join(" ").toLowerCase();
  return haystack.includes(t);
}

export function GlobalSearchButton({ onOpen }) {
  return (
    <button
      data-testid="global-search-trigger"
      onClick={onOpen}
      title="Search areas (⌘K)"
      className="w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center border border-amber-700/40 bg-black/40 hover:bg-amber-900/30 hover:border-amber-500 text-amber-300 hover:text-amber-100 transition-colors"
    >
      <Search size={16} />
    </button>
  );
}

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  // Filter + cap results
  const results = useMemo(() => {
    const filtered = AREAS.filter((a) => matches(a, q));
    return filtered.slice(0, 60);
  }, [q]);

  // Auto-focus input on open · reset cursor on query change.
  // queueMicrotask() defers the state writes so the lint rule
  // (react-hooks/set-state-in-effect) doesn't fire — the effect itself
  // doesn't synchronously trigger a re-render.
  useEffect(() => {
    if (!open) return;
    queueMicrotask(() => {
      setQ("");
      setCursor(0);
    });
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);
  useEffect(() => {
    queueMicrotask(() => setCursor(0));
  }, [q]);

  // ⌨️ Keyboard: Esc close · ↑/↓ navigate · Enter select
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") { onClose?.(); return; }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      } else if (e.key === "Enter") {
        const r = results[cursor];
        if (r) window.location.href = r.path;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, results, cursor, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="global-search-modal"
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/85 backdrop-blur-sm px-4 pt-[10vh]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-2xl border-2 border-amber-700/60 bg-gradient-to-br from-stone-950 via-black to-stone-900 shadow-[0_0_60px_rgba(217,119,6,0.25)]"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-amber-900/40">
          <Search size={18} className="text-amber-400 shrink-0" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a game area — diner, gym, codex, coliseum…"
            className="flex-1 bg-transparent outline-none text-amber-100 placeholder:text-neutral-500 text-base"
          />
          <button
            data-testid="global-search-close"
            onClick={onClose}
            className="text-neutral-500 hover:text-amber-300 transition-colors"
            title="Close (Esc)"
          >
            <X size={18} />
          </button>
        </div>
        <div
          data-testid="global-search-results"
          className="max-h-[60vh] overflow-y-auto p-2"
        >
          {results.length === 0 && (
            <div data-testid="global-search-empty" className="px-4 py-8 text-center text-neutral-500 text-sm">
              No areas match <span className="text-amber-300 font-mono">&quot;{q}&quot;</span>.
              <div className="text-[11px] mt-2 text-neutral-600">Try: diner · gym · codex · wilds · coliseum</div>
            </div>
          )}
          {results.map((a, i) => (
            <a
              key={a.id}
              href={a.path}
              data-testid={`global-search-result-${a.id}`}
              onMouseEnter={() => setCursor(i)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                i === cursor
                  ? "bg-amber-900/40 border border-amber-500/40"
                  : "border border-transparent hover:bg-amber-900/20"
              }`}
            >
              <div className="text-2xl shrink-0">{a.icon}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-black text-amber-100 truncate">{a.label}</div>
                <div className="text-[11px] text-neutral-400 truncate">{a.desc}</div>
              </div>
              <div className="text-[10px] font-mono text-neutral-600 hidden sm:block shrink-0">{a.path}</div>
            </a>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-amber-900/30 text-[10px] text-neutral-500 flex items-center justify-between">
          <div>
            <kbd className="px-1.5 py-0.5 rounded bg-stone-800 border border-stone-700 mr-1">↑</kbd>
            <kbd className="px-1.5 py-0.5 rounded bg-stone-800 border border-stone-700 mr-2">↓</kbd>
            navigate ·
            <kbd className="px-1.5 py-0.5 rounded bg-stone-800 border border-stone-700 mx-1">⏎</kbd>
            open ·
            <kbd className="px-1.5 py-0.5 rounded bg-stone-800 border border-stone-700 mx-1">Esc</kbd>
            close
          </div>
          <div className="hidden sm:block">{AREAS.length} total areas</div>
        </div>
      </div>
    </div>
  );
}
